import { Dialog, DialogPanel, DialogTitle } from '@headlessui/react';
import { STATUS_LABELS } from './KnowledgeStatusBadge';
import type { KnowledgeDocumentStatus } from '../../types';

/**
 * The name-collision prompt (task 1084).
 *
 * The service refuses a second live document with the same name (409
 * `NAME_CONFLICT`). Instead of showing that refusal raw, both upload surfaces
 * ask the User what they actually want. Callers decide WHICH choices are
 * offered and in what order — the sidebar leads with Attach existing, the
 * Documents page has no use for it — but Discard is always last, because it is
 * the choice that does nothing.
 *
 * A HeadlessUI `Dialog`, so it renders in a portal: the sidebar's prompt has to
 * sit above the page, not inside the docked pane's stacking context. Dismissing
 * it (Escape, a click outside) is Discard, the choice that changes nothing.
 */
export type KnowledgeDocumentConflictChoice =
  | 'attach-existing'
  | 'replace'
  | 'keep-both'
  | 'discard';

const CHOICE_LABELS: Record<KnowledgeDocumentConflictChoice, string> = {
  'attach-existing': 'Attach existing',
  replace: 'Replace',
  'keep-both': 'Keep both',
  discard: 'Discard',
};

const CHOICE_HINTS: Record<KnowledgeDocumentConflictChoice, string> = {
  'attach-existing': 'Use the document already in your library. Nothing is uploaded.',
  replace: 'Swap the existing document’s file for this one and re-index it.',
  'keep-both': 'Add this file as a second document with a numbered name.',
  discard: 'Drop this file. Nothing changes.',
};

interface KnowledgeDocumentConflictDialogProps {
  isOpen: boolean;
  /** The colliding file's name — the dialog's title. */
  fileName: string;
  /** Status of the document already holding the name. */
  existingStatus: KnowledgeDocumentStatus;
  choices: KnowledgeDocumentConflictChoice[];
  onChoose: (choice: KnowledgeDocumentConflictChoice) => void;
}

export function KnowledgeDocumentConflictDialog({
  isOpen,
  fileName,
  existingStatus,
  choices,
  onChoose,
}: KnowledgeDocumentConflictDialogProps) {
  if (!isOpen) return null;

  // Caller order, except that Discard always closes the list.
  const ordered = [
    ...choices.filter((choice) => choice !== 'discard'),
    ...(choices.includes('discard') ? (['discard'] as const) : []),
  ];

  // Only a Ready document can ride a turn, so Attach existing is offered but
  // held, with the status as the reason, while the existing one is not Ready.
  const attachBlockedReason =
    existingStatus === 'ready'
      ? null
      : `The existing document is ${(STATUS_LABELS[existingStatus] ?? existingStatus).toLowerCase()}, so it cannot be attached yet.`;

  return (
    <Dialog open onClose={() => onChoose('discard')} className="relative z-[60]">
      <div className="fixed inset-0 bg-black bg-opacity-50" aria-hidden="true" />
      <div className="fixed inset-0 flex items-center justify-center">
        <DialogPanel
          data-testid="knowledge-document-conflict-dialog"
          className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full mx-4"
        >
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <DialogTitle
              as="h3"
              className="text-lg font-medium text-gray-900 dark:text-white break-all"
            >
              {fileName}
            </DialogTitle>
            <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
              A document with this name is already in your library.
            </p>
          </div>
          <div className="px-6 py-4 space-y-2">
            {ordered.map((choice) => {
              const disabled = choice === 'attach-existing' && attachBlockedReason !== null;
              const isDiscard = choice === 'discard';
              return (
                <button
                  key={choice}
                  type="button"
                  onClick={() => onChoose(choice)}
                  disabled={disabled}
                  title={disabled ? (attachBlockedReason ?? undefined) : CHOICE_HINTS[choice]}
                  data-testid={`knowledge-conflict-${choice}`}
                  className={`w-full text-left px-4 py-2 rounded-md border text-sm disabled:opacity-50 disabled:cursor-not-allowed ${
                    isDiscard
                      ? 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                      : 'border-indigo-200 dark:border-indigo-800 text-indigo-700 dark:text-indigo-300 hover:bg-indigo-50 dark:hover:bg-indigo-900/30'
                  }`}
                >
                  <span className="block font-medium">{CHOICE_LABELS[choice]}</span>
                  <span className="block text-xs text-gray-500 dark:text-gray-400">
                    {disabled ? attachBlockedReason : CHOICE_HINTS[choice]}
                  </span>
                </button>
              );
            })}
          </div>
        </DialogPanel>
      </div>
    </Dialog>
  );
}

export default KnowledgeDocumentConflictDialog;
